import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { webApi } from "@/lib/webApi";
import { HeroStudio } from "@/components/admin/HeroStudio";

const DEFAULT_IMG = "https://images.unsplash.com/photo-1779154137940-a251f8c92b10?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const DEFAULT_SUB = "Fresh pieces and beauty finds from Lamka's sellers, added daily.";
const mediaSrc = (url) => `${process.env.REACT_APP_BACKEND_URL}${url}`;

const Headline = ({ text }) => {
  if (!text)
    return <>New Arrivals <span className="font-sans font-light text-[#C5A059]">—</span> <em className="text-[#C5A059]">Beauty & Fashion</em></>;
  const [a, ...rest] = text.split(" — ");
  return rest.length ? <>{a} <span className="font-sans font-light text-[#C5A059]">—</span> <em className="text-[#C5A059]">{rest.join(" — ")}</em></> : text;
};

export const Hero = ({ onExplore }) => {
  const { isAdmin } = useAuth();
  const [hero, setHero] = useState(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(() => webApi.get("/hero").then((r) => setHero(r.data)).catch(() => {}).finally(() => setReady(true)), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  // Switch automatically when a live scheduled banner ends
  useEffect(() => {
    if (!hero?.ends_at) return;
    const ms = new Date(hero.ends_at) - Date.now();
    if (ms <= 0 || ms > 2 ** 31 - 1) return;
    const t = setTimeout(load, ms + 1000);
    return () => clearTimeout(t);
  }, [hero?.ends_at, load]);

  const custom = hero?.url;
  return (
    <section data-testid="hero-banner" className="relative w-full h-[72svh] sm:h-[78vh] min-h-[480px] sm:min-h-[520px] overflow-hidden bg-[#0F0F0F]">
      {ready && (custom && hero.media_type === "video" ? (
        <video data-testid="hero-video" key={custom} src={mediaSrc(custom)} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover fade-up" />
      ) : (
        <img data-testid="hero-image" key={custom || "default"} src={custom ? mediaSrc(custom) : DEFAULT_IMG} srcSet={custom ? undefined : `${DEFAULT_IMG.replace("w=2000", "w=800")} 800w, ${DEFAULT_IMG.replace("w=2000", "w=1400")} 1400w, ${DEFAULT_IMG} 2000w`} sizes="100vw" fetchpriority="high" alt="New arrivals" className="absolute inset-0 h-full w-full object-cover object-[center_30%] animate-slow-zoom" />
      ))}
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent" />
      {isAdmin && <HeroStudio hero={hero} onSaved={(d) => d && setHero(d)} refresh={load} />}
      <div className="relative h-full flex flex-col justify-end px-4 sm:px-8 lg:px-12 pb-16 lg:pb-24 max-w-5xl">
        <p className="eyebrow mb-6 fade-up">Season Edit · Lamka</p>
        <h1 data-testid="hero-title" className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight fade-up [animation-delay:120ms] break-words">
          {ready && <Headline text={hero?.headline} />}
        </h1>
        <p data-testid="hero-subtext" className="text-white/70 text-sm sm:text-base max-w-lg mt-6 fade-up [animation-delay:240ms]">
          {ready && (hero?.subtext || DEFAULT_SUB)}
        </p>
        <button
          data-testid="hero-explore-button"
          onClick={onExplore}
          className="mt-10 self-start border border-[#C5A059] text-[#C5A059] px-8 py-3 text-xs tracking-[0.3em] uppercase transition-[background-color,color,box-shadow] duration-300 hover:bg-[#C5A059] hover:text-[#0F0F0F] hover:shadow-[0_0_30px_rgba(197,160,89,0.4)] fade-up [animation-delay:360ms]"
        >
          {hero?.button_text || "Explore the edit"}
        </button>
      </div>
    </section>
  );
};
